import { useState, useEffect } from "react";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { Briefcase, DollarSign, Play, Search, CheckCircle } from "lucide-react";
import EmptyState from "@/components/EmptyState";

export default function JobsSection() {
  const { t, language } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = [
    { id: "all", label: t("الكل", "All") },
    { id: "warehouse", label: t("مستودعات", "Warehouse") },
    { id: "delivery", label: t("توصيل", "Delivery") },
    { id: "food", label: t("مطاعم", "Food") },
    { id: "construction", label: t("بناء", "Construction") },
    { id: "rideshare", label: t("أوبر/ليفت", "Rideshare") }
  ];

  useEffect(() => {
    loadJobs();
  }, [selectedCategory]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== "all" ? { category: selectedCategory } : {};
      const response = await axios.get(`${API}/content/jobs`, { params });
      setJobs(response.data);
    } catch (error) {
      console.error("Failed to load jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title={t("فرص العمل", "Jobs")}>
      <div className="space-y-6" data-testid="jobs-section">
        {/* Header */}
        <div className="glass-card rounded-2xl p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-orange-500/10 blur-[100px] rounded-full"></div>
          <div className="relative">
            <Badge className="bg-orange-500/20 text-orange-400 mb-4">$2/{t("شهرياً", "month")}</Badge>
            <h2 className="text-2xl font-bold text-white mb-2">{t("اكتشف فرص العمل", "Discover Job Opportunities")}</h2>
            <p className="text-slate-400">{t("وظائف مناسبة للمغتربين مع فيديوهات توضيحية", "Jobs for expats with tutorial videos")}</p>
          </div>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <Button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`rounded-full ${
                selectedCategory === cat.id 
                  ? 'btn-primary' 
                  : 'btn-secondary'
              }`}
              data-testid={`category-${cat.id}`}
            >
              {cat.label}
            </Button>
          ))}
        </div>

        {/* Jobs Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass-card rounded-xl overflow-hidden">
                <div className="h-48 skeleton"></div>
                <div className="p-5 space-y-3">
                  <div className="h-6 skeleton rounded w-3/4"></div>
                  <div className="h-4 skeleton rounded w-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <EmptyState
            icon={Briefcase}
            title_ar="لا توجد وظائف في هذه الفئة"
            title_en="No jobs in this category"
            body_ar="جرّب فئة أخرى أو ارجع لاحقاً — نحدّث القائمة باستمرار."
            body_en="Try another category or check back later — we update jobs frequently."
            cta_ar="عرض كل الوظائف"
            cta_en="Show all jobs"
            onCta={() => setSelectedCategory("all")}
            testid="jobs-empty"
          />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.map((job) => (
              <div key={job.job_id} className="glass-card rounded-xl overflow-hidden group" data-testid={`job-${job.job_id}`}>
                {/* Video Thumbnail */}
                <div className="relative h-48 bg-slate-800 flex items-center justify-center">
                  <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center cursor-pointer transition-transform group-hover:scale-110">
                    <Play className="fill-black text-black" size={28} />
                  </div>
                  {!job.video_url && (
                    <div className="absolute bottom-3 right-3 bg-slate-900/90 text-slate-300 text-xs px-2 py-1 rounded">
                      {t("الفيديو قريباً", "Video Coming Soon")}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-lg text-white">{job.title_ar}</h3>
                      <p className="text-sm text-slate-400">{job.title}</p>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400">{t("متاح", "Available")}</Badge>
                  </div>

                  <p className="text-slate-400 text-sm mb-4">{job.description_ar}</p>

                  {/* Hourly Rate */}
                  <div className="flex items-center gap-2 mb-4 p-3 bg-slate-800 rounded-lg">
                    <DollarSign className="text-green-400" size={20} />
                    <span className="font-bold text-green-400">
                      ${job.hourly_rate_min} - ${job.hourly_rate_max}
                    </span>
                    <span className="text-slate-500 text-sm">/{t("ساعة", "hour")}</span>
                  </div>

                  {/* Requirements */}
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-white">{t("المتطلبات:", "Requirements:")}</p>
                    <ul className="space-y-1">
                      {job.requirements_ar.slice(0, 3).map((req, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-slate-400">
                          <CheckCircle className="text-yellow-500" size={14} />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
